import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onViewDetails }) => {
  const handleLiveDemo = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
 
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-all duration-300 group"
    >
      {/* Project Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute top-3 right-3">
          <span className={`px-3 py-1.5 text-xs font-semibold rounded-lg backdrop-blur-md transition-all duration-200 border ${project.status === 'completed'
            ? 'bg-success/20 text-success hover:bg-success/30 border-success'
            : project.status === 'in-progress'
              ? 'bg-warning/20 text-warning hover:bg-warning/30 border-warning'
              : 'bg-muted/20 text-muted-foreground hover:bg-muted/30 border-muted-foreground/50'
            }`}>
            {project.status === 'completed' ? 'Completed' :
              project.status === 'in-progress' ? 'In Progress' : 'Planned'}
          </span>
        </div>
      </div>

      {/* Project Content */}
      <div className="p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-lg font-semibold text-text-primary group-hover:text-primary transition-colors duration-200 flex-1 min-w-0 break-words">
            {project.title}
          </h3>
          <div className="flex items-center space-x-1 text-xs text-muted-foreground flex-shrink-0 whitespace-nowrap">
            <Icon name="Calendar" size={12} />
            <span>{project.completedDate}</span>
          </div>
        </div>

        <p className="text-sm text-text-secondary mb-4 line-clamp-3">
          {project.description}
        </p>

        {/* Technology Stack */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {project.technologies.slice(0, 4).map((tech, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs font-medium bg-muted text-muted-foreground rounded-md"
              >
                {tech}
              </span>
            ))}
            {project.technologies.length > 4 && (
              <span className="px-2 py-1 text-xs font-medium bg-muted text-muted-foreground rounded-md">
                +{project.technologies.length - 4} more
              </span>
            )}
          </div>
        </div>

        {/* Project Stats */}
        <div className="flex items-center justify-between mb-4 text-xs text-muted-foreground">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={12} />
              <span>{project.stars}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="GitFork" size={12} />
              <span>{project.forks}</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Clock" size={12} />
            <span>{project.duration}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLiveDemo(project.liveUrl)}
            iconName="ExternalLink"
            iconPosition="right"
            iconSize={14}
            className="flex-1"
          >
            Live Demo
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => onViewDetails(project)}
            iconName="Eye"
            iconPosition="right"
            iconSize={14}
            className="flex-1"
          >
            View Details
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectCard;